import React, { useRef } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import NightShelf, { type NightShelfProps } from '@/components/market/NightShelf'

/**
 * The night panel on a phone: a bottom sheet (Radix Dialog, so focus is trapped, Esc and the
 * backdrop close it, and focus returns to the row that opened it). Same content as the desktop
 * shelf; the night's name is the sheet's title.
 */
export default function NightSheet({
  open,
  onOpenChange,
  ...shelf
}: NightShelfProps & { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { night } = shelf
  // Opened from a row or a card, not a Radix trigger, so the dialog would return focus to nothing.
  // Remember what had focus as it opened, and hand focus back to it on close.
  const returnTo = useRef<HTMLElement | null>(null)
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="overlay-motion fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onOpenAutoFocus={() => {
            returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            returnTo.current?.focus({ preventScroll: true })
          }}
          className="drawer-motion-bottom fixed inset-x-0 bottom-0 z-50 flex max-h-[88dvh] flex-col rounded-t-2xl border-t border-border/60 bg-white shadow-2xl outline-none"
        >
          <div className="flex shrink-0 justify-center pt-2.5" aria-hidden="true">
            <span className="h-1 w-10 rounded-full bg-slate-200" />
          </div>
          <div className="flex shrink-0 items-start justify-between gap-3 px-4 pb-3 pt-2">
            <DialogPrimitive.Title className="text-base font-semibold tracking-tight text-foreground">
              {night.isToday ? `Tonight, ${night.label}` : night.label}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              aria-label="Close"
              className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
            <NightShelf {...shelf} hideTitle />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
